import { useCallback, useEffect, useState } from "react";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { useAnchorEntitlements } from "@/contexts/AnchorEntitlementsContext";
import type { TemplateFamily, TemplateSubscriptionMeta, TemplateVersion } from "@/lib/sign/signTemplateApiClient";
import { listSignTemplates, upsertSignTemplate } from "@/lib/sign/signTemplateApiClient";
import { turboSignEntitlementEnabled } from "@/lib/sign/turboSignEntitlement";
import { DEFAULT_SIGNER_ROLE_PRESETS } from "@/lib/sign/templateEditorModel";
import { consumePendingSignTemplateVersionId } from "@/lib/workspaceModeNavigation";
import { SignTemplateEditor } from "./SignTemplateEditor";

type FamilyEntry = {
  family: TemplateFamily;
  versions: TemplateVersion[];
  subscription?: TemplateSubscriptionMeta;
};

type EditorTarget = {
  family: TemplateFamily;
  version: TemplateVersion;
  subscription?: TemplateSubscriptionMeta;
};

export function SignTemplatesPanel() {
  const { anchors } = useAnchorEntitlements();
  const turboSignEnabled = turboSignEntitlementEnabled(anchors);
  const [families, setFamilies] = useState<FamilyEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [editor, setEditor] = useState<EditorTarget | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    const r = await listSignTemplates();
    setLoading(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setFamilies(r.data.families);
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    const pendingVersionId = consumePendingSignTemplateVersionId();
    if (!pendingVersionId || families.length === 0) return;
    for (const entry of families) {
      const version = entry.versions.find((v) => v.id === pendingVersionId);
      if (version) {
        setEditor({ family: entry.family, version, subscription: entry.subscription });
        return;
      }
    }
    void (async () => {
      const r = await listSignTemplates();
      if (!r.ok) return;
      for (const entry of r.data.families) {
        const version = entry.versions.find((v) => v.id === pendingVersionId);
        if (version) {
          setFamilies(r.data.families);
          setEditor({ family: entry.family, version, subscription: entry.subscription });
          return;
        }
      }
    })();
  }, [families]);

  async function handleCreate() {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    setError(null);
    const r = await upsertSignTemplate({
      name,
      document_class: "rto_contract",
      sale_path: "rto_company",
      signer_roles: DEFAULT_SIGNER_ROLE_PRESETS,
      field_map: { fields: [] },
    });
    setCreating(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setNewName("");
    setEditor({ family: r.data.family, version: r.data.version });
    void reload();
  }

  function openDraft(entry: FamilyEntry) {
    const { family, versions, subscription } = entry;
    const draft = subscription ? undefined : versions.find((v) => v.status === "draft");
    const published = versions.find((v) => v.status === "published");
    const version = draft ?? published ?? versions[0];
    if (!version) return;
    setEditor({ family, version, subscription });
  }

  if (editor) {
    return (
      <SignTemplateEditor
        family={editor.family}
        version={editor.version}
        subscribedReadOnly={Boolean(editor.subscription?.read_only)}
        onClose={() => setEditor(null)}
        onSaved={(family, version) => {
          setEditor({ family, version });
          void reload();
        }}
      />
    );
  }

  return (
    <div {...{ [FIREFLY_TARGET_ATTR]: "ff-sign-templates" }} className="space-y-4">
      {!turboSignEnabled ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
          Turbo Sign is not enabled for this organization. Contact your admin to add the Turbo Sign
          entitlement (or Turbo Sales bundle).
        </div>
      ) : null}
      <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">New sign template</h3>
        <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
          Upload a contract PDF, drag signature and data fields, then publish for sales send flows.
        </p>
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <label className="min-w-[14rem] flex-1 text-xs">
            Template name
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Bennett RTO contract"
              className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-800"
            />
          </label>
          <button
            type="button"
            disabled={creating || !newName.trim() || !turboSignEnabled}
            onClick={() => void handleCreate()}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {creating ? "Creating…" : "Create draft"}
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/30">
          {error}
        </div>
      ) : null}

      {loading ? (
        <p className="text-sm text-gray-500">Loading templates…</p>
      ) : families.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">No sign templates yet.</p>
      ) : (
        <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white dark:divide-zinc-800 dark:border-zinc-700 dark:bg-zinc-900">
          {families.map((entry) => {
            const { family, versions, subscription } = entry;
            const draft = subscription ? undefined : versions.find((v) => v.status === "draft");
            const published = versions.find((v) => v.status === "published");
            return (
              <li key={family.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
                <div>
                  <div className="text-sm font-medium text-gray-900 dark:text-zinc-100">{family.name}</div>
                  <div className="text-xs text-gray-500 dark:text-zinc-400">
                    {family.document_class}
                    {family.sale_path ? ` · ${family.sale_path}` : ""}
                    {subscription ? " · subscribed (read-only)" : ""}
                    {subscription?.mandatory ? " · mandatory" : ""}
                    {published ? ` · published v${published.version_number}` : ""}
                    {draft ? ` · draft v${draft.version_number}` : ""}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => openDraft(entry)}
                  className="rounded-md border border-gray-300 px-2 py-1 text-xs font-medium dark:border-zinc-600"
                >
                  {subscription ? "View" : draft ? "Edit draft" : "View"}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
